import { cn } from "@au-schein-de/au-ui-library";
import {
	DELIVERY_METHOD_KIND,
	type DeliveryMethodKind,
} from "@dransay/api/models";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { DeliveryMethodSelector } from "~/components/delivery-method-selector/delivery-method-selector.component";
import { Logo } from "~/components/logo.component";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import { FULFILLMENT_GROUP_KIND } from "~/features/checkout/delivery/fulfillment-groups.resolver";
import type { DeliveryAddressCookieData } from "~/features/delivery-address/delivery-address.cookie";
import { ExpressAvailabilityHeaderCardHost } from "~/features/products/express-availability-header-card-host.component";
import { SearchBar } from "~/features/products/product-list.search-bar.component";
import { useHeaderCompaction } from "~/hooks/use-header-compaction.hook";
import type { SessionUser } from "~/tooling/session";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import {
	deliveryMethodSelectedEvent,
	navigationClickedEvent,
} from "~/tooling/telemetry/events";
import { HeaderFavoritesButton } from "../auth/auth.header-favorites-button.component";
import { UserProfileButton } from "../auth/auth.user-profile-button.component";
import { Cart } from "../cart/cart.component";
import { buildProductsHref } from "./build-products-href";

const HIDDEN_HEADER_METHODS: ReadonlyArray<DeliveryMethodKind> = [
	DELIVERY_METHOD_KIND.EXPRESS_COURIER,
];

interface StorefrontHeaderProps {
	logoVariant: "default" | "navigation";
	user?: SessionUser;
	deliveryAddress?: DeliveryAddressCookieData | null;
	onOpenAddressModal?: () => void;
	expressPilotVendorIds?: readonly string[];
	enablePlpExpressAvailabilityModal?: boolean;
}

export function StorefrontHeader({
	logoVariant,
	user,
	deliveryAddress,
	onOpenAddressModal,
	expressPilotVendorIds = [],
	enablePlpExpressAvailabilityModal = false,
}: StorefrontHeaderProps) {
	const [searchParams] = useSearchParams();
	const { t } = useTranslation("common");
	const { containerRef, compaction } = useHeaderCompaction<HTMLElement>();
	const {
		selectedVendorId,
		selectedDeliveryMethodKind,
		setSelectedDeliveryMethodKind,
	} = useVendorSelection();

	const productsHref = buildProductsHref({
		searchParams,
		vendorId: selectedVendorId,
		deliveryMethod: selectedDeliveryMethodKind,
	});

	const onMethodSelect = async (
		method: DeliveryMethodKind,
		closePopover: () => void,
	) => {
		captureEvent(
			deliveryMethodSelectedEvent(method, FULFILLMENT_GROUP_KIND.MEDICATION),
		);
		try {
			await setSelectedDeliveryMethodKind(method);
		} finally {
			closePopover();
		}
	};

	return (
		<header
			ref={containerRef}
			data-testid="storefront-header-desktop-shell"
			className={cn(
				"hidden md:flex sticky top-0 h-(--storefront-header-height-desktop) w-full items-center border-b border-ansay-900 bg-ansay-900 px-6 lg:px-14 py-6 backdrop-blur-[12px] z-(--z-index-cart-dialog-content)",
			)}
		>
			<a
				id="header-logo-link"
				href={productsHref}
				aria-label={t("header.logo.label", "Go to products")}
				className="flex h-8 w-[102px] shrink-0 items-center"
				onClick={(e) => {
					const elementId = e.currentTarget.id;
					captureEvent(navigationClickedEvent(elementId, "", "main"));
				}}
			>
				<Logo size={32} variant="white" />
				{logoVariant === "navigation" && (
					<span className="pb-2 text-caption-emphasis text-white">UK</span>
				)}
			</a>

			<div className="ml-6 lg:ml-12 flex min-h-px min-w-px flex-1 items-center gap-4">
				<DeliveryMethodSelector
					selectedMethod={selectedDeliveryMethodKind ?? "shipping"}
					onMethodSelect={onMethodSelect}
					deliveryMethodLabel={t("header.deliveryMethod.label")}
					getMethodLabel={(method) => t(`header.deliveryMethod.${method}`)}
					addressLabel={t("header.address.label")}
					addressText={
						deliveryAddress?.displayString ?? t("header.address.placeholder")
					}
					isAddressPlaceholder={!deliveryAddress}
					addressActionLabel={
						deliveryAddress
							? t("header.address.change")
							: t("header.address.apply")
					}
					onAddressAction={onOpenAddressModal}
					hiddenMethods={HIDDEN_HEADER_METHODS}
					expressPromoSlot={
						<ExpressAvailabilityHeaderCardHost
							enabled={
								enablePlpExpressAvailabilityModal &&
								(!user || user.role === "customer")
							}
							postalCode={deliveryAddress?.postalCode}
							expressVendorIds={expressPilotVendorIds}
							selectedDeliveryMethodKind={
								selectedDeliveryMethodKind ?? "shipping"
							}
						/>
					}
				/>

				<div
					data-testid="storefront-header-search-slot"
					className="flex min-h-px min-w-[140px] flex-1 items-center"
				>
					<SearchBar />
				</div>

				<HeaderFavoritesButton />
				<div data-testid="storefront-header-profile-slot" className="shrink-0">
					<UserProfileButton
						user={user}
						desktopIconOnly={compaction.isProfileIconOnly}
					/>
				</div>
				<div data-testid="storefront-header-cart-slot" className="shrink-0">
					<Cart
						truncateVendorName
						desktopIconOnly={compaction.isCartIconOnly}
					/>
				</div>
			</div>
		</header>
	);
}
